import React, { useEffect, useState } from 'react';
import { MessageCircle } from 'lucide-react';
import { sendSupportMessage, getMySupportMessages, markSupportRepliesSeen } from '../../../shared/api/support';
import { setSupportUnread } from '../supportUnread';

const MESSAGE_MAX = 1000;

const when = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('zh-CN', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Shanghai' });
};

/**
 * 有问题？写给我们 — a message box in the settings sheet plus the family's own
 * threads underneath. Messages page Rob; his replies (written to the database
 * with scripts/support-reply.js) show up here, and opening this sheet marks
 * them seen (which clears the dot on the settings gear).
 */
export default function SupportSection() {
  const [message, setMessage] = useState('');
  const [contact, setContact] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  const [threads, setThreads] = useState([]);

  useEffect(() => {
    let alive = true;
    getMySupportMessages()
      .then((r) => {
        if (!alive) return;
        setThreads(r.messages || []);
        if (r.unread > 0) {
          markSupportRepliesSeen().catch(() => {});
          setSupportUnread(0);
        }
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    const text = message.trim();
    if (text.length < 2) { setError('写一两句再发吧'); return; }
    setSending(true); setError('');
    try {
      const r = await sendSupportMessage({ message: text, contact: contact.trim() || undefined });
      setThreads((prev) => [{ id: r.id, message: text, status: 'open', createdAt: r.createdAt, replies: [] }, ...prev]);
      setSent(true); setMessage(''); setContact('');
    } catch (err) {
      setError(/429|too many|rate/i.test(err.message || '') ? '发得有点多了，过一会儿再试' : (err.message || '没发出去，再试一次'));
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="settings-section settings-support">
      <div className="settings-login__head">
        <MessageCircle size={18} className="settings-install-icon" />
        <span className="settings-install-label">有问题？写给我们</span>
      </div>
      {sent ? (
        <div className="settings-support__done" role="status">
          <p className="settings-support__done-big">收到了！</p>
          <p className="settings-support__done-sub">回复会出现在下面，设置图标上会有个小红点。</p>
          <button type="button" className="settings-login__link" onClick={() => setSent(false)}>再写一条</button>
        </div>
      ) : (
        <form className="settings-support__form" onSubmit={submit}>
          <label className="settings-support__label" htmlFor="support-message">哪里不对，或者想要什么？</label>
          <textarea id="support-message" className="settings-support__text" value={message} maxLength={MESSAGE_MAX} rows={3}
            placeholder="比如：视频打不开、密码忘了、想换手机号……" onChange={(e) => { setMessage(e.target.value); setError(''); }} />
          <input className="settings-login__input" value={contact} maxLength={80} placeholder="怎么联系你？微信号或手机号（选填）" onChange={(e) => setContact(e.target.value)} />
          <div className="settings-login__actions">
            <button type="submit" className="settings-install-btn" disabled={sending}>{sending ? '发送中...' : '发送'}</button>
            <span className="settings-support__hint">会直接发到我们手机上</span>
          </div>
          {error && <p className="settings-login__error">{error}</p>}
        </form>
      )}

      {threads.length > 0 && (
        <ol className="settings-support__threads" aria-label="我的消息">
          {threads.map((t) => (
            <li key={t.id} className="settings-support__thread">
              <div className="settings-support__bubble settings-support__bubble--me">
                <span className="settings-support__meta">你 · {when(t.createdAt)}</span>
                <p>{t.message}</p>
              </div>
              {t.replies.map((r) => (
                <div key={r.id} className="settings-support__bubble settings-support__bubble--them">
                  <span className="settings-support__meta">小羊老师 · {when(r.createdAt)}</span>
                  <p>{r.text}</p>
                </div>
              ))}
              {t.replies.length === 0 && <span className="settings-support__pending">已收到，等回复</span>}
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
